import { PRIVILEGES } from 'constants/privileges';
import { useMemo, useState } from 'react';
import { AlertService, InvoiceService } from 'api';
import { Alert } from 'api/alerts/alerts.types';
import { StyledButton } from 'components/Buttons';
import { LoaderFullScreen } from 'components/Loaders/LoaderFullScreen';
import {
  getAllAlertsState,
  useAlerts,
} from 'contexts/alertsContext/alertsContext';
import { useAuth } from 'contexts/authContext';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { reactPaths, InvoiceTab } from 'root/pathnames';
import { hasPrivilege } from 'utils/authFunctions.utils';
import {
  canAddInvoice,
  canBatchMarkAdministrativeAsPaid,
  canBatchMarkForeignAsPaid,
  canBatchMarkOwnAsPaid,
  isDeleteEnabled,
} from 'utils/invoiceFunctions.utils';
import { appToast } from 'utils/toast.utils';
import { Invoice } from '../../../../invoices.types';
import { TableData } from '../../invoicesTable.types';
import { ConfirmCancelDialog } from './components/ConfirmCancelDialog';
import { ConfirmExportDialog } from './components/ConfirmExportDialog';
import * as S from './InvoiceTableActions.css';

type Props = {
  selectedInvoices: TableData[];
  onAction: () => Promise<void>;
  onSaldeoErrors: (alerts: Alert[]) => void;
};

export const InvoiceTableActions = ({
  selectedInvoices,
  onAction,
  onSaldeoErrors,
}: Props) => {
  const { user, allowedReplacements, allowedReplacementsByCode } = useAuth();
  const { dispatch } = useAlerts();
  const { tab: invoiceTab } = useParams<{ tab: InvoiceTab }>();

  const [confirmCancelDialogOpen, setConfirmCancelDialogOpen] = useState(false);
  const [confirmExportDialogOpen, setConfirmExportDialogOpen] = useState(false);

  const { t } = useTranslation(['misc', 'invoice']);

  const { loading, changeInvoiceStatus } =
    InvoiceService.useChangeInvoiceStatus();
  const { loading: saldeoLoading, fetchSaldeoDocuments } =
    InvoiceService.useFetchSaldeoDocuments();
  const { loading: enovaLoading, fetchEnovaDocuments } =
    InvoiceService.useFetchEnovaDocuments();

  const navigate = useNavigate();

  const handleSettleInvoices = async () => {
    const invoicesToSettle = selectedInvoices
      .filter((i) => !i.isCorrective)
      .map((i) => i.id);
    const correctiveInvoicesToSettle = selectedInvoices
      .filter((i) => i.isCorrective)
      .map((i) => i.id);

    invoicesToSettle.length &&
      (await changeInvoiceStatus(invoicesToSettle, false, true));
    correctiveInvoicesToSettle.length &&
      (await changeInvoiceStatus(correctiveInvoicesToSettle, true, true));
    await onAction();
  };

  const isGatewayTimeout = (data: any) =>
    typeof data === 'string' && data.includes('504 Gateway Time-out');

  const isImportAlreadyRunning = (data: any, system: 'Saldeo' | 'Enova') => {
    if (system === 'Saldeo') {
      return (
        data?.status === 'BAD_REQUEST' &&
        data?.message === 'SaldeoImportAlreadyRunning'
      );
    }
    return (
      data?.status === 'BAD_REQUEST' &&
      data?.message === 'EnovaImportAlreadyRunning'
    );
  };

  const handleFetchDocuments = async (
    fetchFn: () => Promise<any>,
    system: 'Saldeo' | 'Enova',
  ) => {
    if (system === 'Enova') {
      try {
        const response = await fetchFn();
        const { filesFound, filesProcessed, filesFailed } =
          response.data.result;

        if (filesFound === 0) {
          appToast.info(t('enovaNoNewFiles', { ns: 'invoice' }));
          return;
        }

        if (filesFailed > 0) {
          appToast.warning(
            t('enovaFetchPartial', {
              ns: 'invoice',
              filesProcessed,
              filesFound,
            }),
          );
        } else {
          appToast.success(
            t('enovaFetchSuccess', { ns: 'invoice', filesProcessed }),
          );
        }
      } catch (error: any) {
        if (isImportAlreadyRunning(error?.data, system)) {
          appToast.warning(
            t('fetchAlreadyRunning', { ns: 'invoice', systemFrom: system }),
          );
        } else if (error?.response?.status === 504) {
          appToast.info(
            t('fetchTimeout', { ns: 'invoice', systemFrom: system }),
          );
        } else {
          appToast.error(t('enovaFetchError', { ns: 'invoice' }));
        }
      } finally {
        await onAction();
      }
      return;
    }

    const systemFrom = system;
    const systemWith = system;

    try {
      const response = await fetchFn();

      if (isGatewayTimeout(response.data)) {
        appToast.info(t('fetchTimeout', { ns: 'invoice', systemFrom }));
        return;
      }

      const { totalFound, totalSaved, totalErrors } = response.data.result;

      if (totalFound === 0 && totalErrors === 0) {
        appToast.info(t('noInvoicesFound', { ns: 'invoice', systemFrom }));
        return;
      }

      if (totalErrors > 0) {
        appToast.warning(
          t('fetchPartial', { ns: 'invoice', totalSaved, totalFound }),
        );
      } else {
        appToast.success(
          t('fetchSuccess', { ns: 'invoice', count: totalSaved }),
        );
      }
    } catch (error: any) {
      if (isImportAlreadyRunning(error?.data, system)) {
        appToast.warning(
          t('fetchAlreadyRunning', { ns: 'invoice', systemFrom }),
        );
      } else if (error?.response?.status === 504) {
        appToast.info(t('fetchTimeout', { ns: 'invoice', systemFrom }));
      } else {
        appToast.error(t('fetchError', { ns: 'invoice', systemWith }));
      }
    } finally {
      await onAction();
    }
  };

  const canMarkAsPaid = useMemo(() => {
    switch (invoiceTab) {
      case InvoiceTab.OWN:
        return canBatchMarkOwnAsPaid(
          selectedInvoices as unknown[] as Invoice[],
          user,
          allowedReplacementsByCode,
        );
      case InvoiceTab.FOREIGN:
        return canBatchMarkForeignAsPaid(
          selectedInvoices as unknown[] as Invoice[],
          user,
        );
      case InvoiceTab.ADMINISTRATIVE:
        return canBatchMarkAdministrativeAsPaid(
          selectedInvoices as unknown[] as Invoice[],
          user,
          allowedReplacements,
        );
      case InvoiceTab.INTERNAL:
        return canBatchMarkForeignAsPaid(
          selectedInvoices as unknown[] as Invoice[],
          user,
        );
      default:
        return false;
    }
  }, [
    invoiceTab,
    selectedInvoices,
    user,
    allowedReplacements,
    allowedReplacementsByCode,
  ]);

  return (
    <>
      <LoaderFullScreen loading={loading || saldeoLoading} />
      <ConfirmCancelDialog
        open={confirmCancelDialogOpen}
        selectedInvoices={selectedInvoices}
        onClose={() => setConfirmCancelDialogOpen(false)}
        onSubmit={onAction}
      />
      <ConfirmExportDialog
        open={confirmExportDialogOpen}
        selectedInvoices={selectedInvoices}
        onClose={() => setConfirmExportDialogOpen(false)}
        onSubmit={onAction}
      />
      <S.ActionsContainer>
        <StyledButton
          $primary
          disabled={!invoiceTab || !canAddInvoice(invoiceTab, user)}
          onClick={() =>
            navigate(generatePath(reactPaths.invoicesForm, { tab: invoiceTab }))
          }
        >
          {t('button.create', { ns: 'misc' })}
        </StyledButton>
        <StyledButton
          $primary
          disabled={
            !invoiceTab ||
            !isDeleteEnabled[invoiceTab](
              selectedInvoices as unknown as Invoice[],
              user,
              allowedReplacements,
            )
          }
          onClick={() => setConfirmCancelDialogOpen(true)}
        >
          {invoiceTab === InvoiceTab.FOREIGN ||
          invoiceTab === InvoiceTab.INTERNAL
            ? t('button.delete', { ns: 'misc' })
            : t('button.cancel', { ns: 'misc' })}
        </StyledButton>
        {invoiceTab !== InvoiceTab.INTERNAL && (
          <StyledButton
            $primary
            disabled={
              !selectedInvoices.length &&
              hasPrivilege(PRIVILEGES.Invoices.EXPORT_INVOICE, user)
            }
            onClick={() => setConfirmExportDialogOpen(true)}
          >
            {t('button.export', { ns: 'misc' })}
          </StyledButton>
        )}
        <StyledButton
          $primary
          disabled={!canMarkAsPaid}
          onClick={handleSettleInvoices}
        >
          {t('markAsPaid', { ns: 'invoice' })}
        </StyledButton>
        {invoiceTab === InvoiceTab.FOREIGN && (
          <StyledButton
            $primary
            disabled={
              saldeoLoading ||
              !hasPrivilege(PRIVILEGES.Invoices.IMPORT_INVOICE, user)
            }
            onClick={() => handleFetchDocuments(fetchSaldeoDocuments, 'Saldeo')}
          >
            {t('button.saldeoFetch', { ns: 'misc' })}
          </StyledButton>
        )}
        {/* TEMPORARILY DISABLED - until further development in regards of KSeF
         {(invoiceTab === InvoiceTab.OWN ||
          invoiceTab === InvoiceTab.ADMINISTRATIVE) && (
          <StyledButton
            $primary
            disabled={
              enovaLoading ||
              !hasPrivilege(PRIVILEGES.Invoices.IMPORT_INVOICE, user)
            }
            onClick={() => handleFetchDocuments(fetchEnovaDocuments, 'Enova')}
          >
            {t('button.enovaFetch', { ns: 'misc' })}
          </StyledButton>
        )} */}
      </S.ActionsContainer>
    </>
  );
};
